import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { BaseBackResponsePagination } from '@savgroup-front-common/types';

import { DEFAULT_PAGE_SIZE } from './Datatable.constants';

interface UseDatatablePaginationArgs<D> {
  hits: D[];
  hitCount?: number;
  pageIndex: number;
  pageSize: number;
  isClientMode: boolean;
  hasPagination: boolean;
  onPaginationChange?: OnChangeFn<PaginationState>;
  paginationInterval: number[];
}

interface UseDatatablePaginationResult<D> {
  data: D[];
  pagination: PaginationState;
  handlePaginationChange?: OnChangeFn<PaginationState>;
  shouldShowPagination: boolean;
  paginationSummary: BaseBackResponsePagination;
}

export function useDatatablePagination<D>({
  hits,
  hitCount,
  pageIndex,
  pageSize,
  isClientMode,
  hasPagination,
  onPaginationChange,
  paginationInterval,
}: UseDatatablePaginationArgs<D>): UseDatatablePaginationResult<D> {
  const isLocalPagination =
    !isClientMode && !onPaginationChange && hasPagination;
  const isPaginationLocallyDriven = isClientMode || isLocalPagination;

  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize:
      isClientMode && Number.isFinite(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
  });

  const pagination = useMemo(
    () =>
      isPaginationLocallyDriven ? localPagination : { pageIndex, pageSize },
    [isPaginationLocallyDriven, localPagination, pageIndex, pageSize],
  );

  const total = isPaginationLocallyDriven ? hits.length : (hitCount ?? 0);
  const pageCount = Math.ceil(total / pagination.pageSize);
  const hasPaginationInterval = paginationInterval.some(
    (interval) => total >= interval,
  );

  return {
    data: isLocalPagination
      ? hits.slice(
          pagination.pageIndex * pagination.pageSize,
          (pagination.pageIndex + 1) * pagination.pageSize,
        )
      : hits,
    pagination,
    handlePaginationChange: isPaginationLocallyDriven
      ? setLocalPagination
      : onPaginationChange,
    shouldShowPagination:
      hasPagination && (pageCount > 1 || hasPaginationInterval),
    paginationSummary: {
      total,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      pageCount,
    },
  };
}
